import type { ReactNode } from "react";
import { useEffect } from "react";
import { PanelsView } from "@/panels/modules/panels/components/PanelsView";
import { usePanelsStore } from "@/panels/modules/panels/store/panels.store";
import type { AsidePanelConfig, MainPanelConfig, TopbarConfig } from "@/panels/modules/panels/store/panels.store.types";

interface PanelsProps {
    // Slot props (content)
    leftSidebarSlot?: ReactNode;
    topbarSlot?: ReactNode;
    rightSidebarSlot?: ReactNode;
    leftTopASlot?: ReactNode;
    leftTopBSlot?: ReactNode;
    leftBottomASlot?: ReactNode;
    leftBottomBSlot?: ReactNode;
    rightTopASlot?: ReactNode;
    rightTopBSlot?: ReactNode;
    rightBottomASlot?: ReactNode;
    rightBottomBSlot?: ReactNode;

    // Config props - at panel level
    leftSidebarConfig?: Partial<AsidePanelConfig>;
    topbarConfig?: Partial<TopbarConfig>;
    rightSidebarConfig?: Partial<AsidePanelConfig>;
    leftTopConfig?: Partial<MainPanelConfig>;
    leftBottomConfig?: Partial<MainPanelConfig>;
    rightTopConfig?: Partial<MainPanelConfig>;
    rightBottomConfig?: Partial<MainPanelConfig>;

    // Behavior props
    lockMainPanelResize?: boolean;
}

export const Panels = ({
    // Slot props
    leftSidebarSlot,
    topbarSlot,
    rightSidebarSlot,
    leftTopASlot,
    leftTopBSlot,
    leftBottomASlot,
    leftBottomBSlot,
    rightTopASlot,
    rightTopBSlot,
    rightBottomASlot,
    rightBottomBSlot,

    // Config props
    leftSidebarConfig,
    topbarConfig,
    rightSidebarConfig,
    leftTopConfig,
    leftBottomConfig,
    rightTopConfig,
    rightBottomConfig,

    // Behavior props
    lockMainPanelResize = false,
}: PanelsProps) => {
    // Get full configs from store
    const leftSidebarStoreConfig = usePanelsStore((s) => s.leftSidebar);
    const topbarStoreConfig = usePanelsStore((s) => s.topbar);
    const rightSidebarStoreConfig = usePanelsStore((s) => s.rightSidebar);
    const leftTopStoreConfig = usePanelsStore((s) => s.leftTop);
    const leftBottomStoreConfig = usePanelsStore((s) => s.leftBottom);
    const rightTopStoreConfig = usePanelsStore((s) => s.rightTop);
    const rightBottomStoreConfig = usePanelsStore((s) => s.rightBottom);

    const setAsidePanelConfig = usePanelsStore((s) => s.setAsidePanelConfig);
    const setTopbarConfig = usePanelsStore((s) => s.setTopbarConfig);
    const setMainPanelConfig = usePanelsStore((s) => s.setMainPanelConfig);

    useEffect(() => {
        if (!leftSidebarConfig) return;
        setAsidePanelConfig("leftSidebar", leftSidebarConfig);
    }, [leftSidebarConfig, setAsidePanelConfig]);
    useEffect(() => {
        if (!topbarConfig) return;
        setTopbarConfig(topbarConfig);
    }, [topbarConfig, setTopbarConfig]);
    useEffect(() => {
        if (!rightSidebarConfig) return;
        setAsidePanelConfig("rightSidebar", rightSidebarConfig);
    }, [rightSidebarConfig, setAsidePanelConfig]);
    useEffect(() => {
        if (!leftTopConfig) return;
        setMainPanelConfig("leftTop", leftTopConfig);
    }, [leftTopConfig, setMainPanelConfig]);
    useEffect(() => {
        if (!leftBottomConfig) return;
        setMainPanelConfig("leftBottom", leftBottomConfig);
    }, [leftBottomConfig, setMainPanelConfig]);
    useEffect(() => {
        if (!rightTopConfig) return;
        setMainPanelConfig("rightTop", rightTopConfig);
    }, [rightTopConfig, setMainPanelConfig]);
    useEffect(() => {
        if (!rightBottomConfig) return;
        setMainPanelConfig("rightBottom", rightBottomConfig);
    }, [rightBottomConfig, setMainPanelConfig]);

    return (
        <PanelsView
            leftSidebar={leftSidebarSlot}
            topbar={topbarSlot}
            rightSidebar={rightSidebarSlot}
            leftTopA={leftTopASlot}
            leftTopB={leftTopBSlot}
            leftBottomA={leftBottomASlot}
            leftBottomB={leftBottomBSlot}
            rightTopA={rightTopASlot}
            rightTopB={rightTopBSlot}
            rightBottomA={rightBottomASlot}
            rightBottomB={rightBottomBSlot}
            leftSidebarConfig={leftSidebarStoreConfig}
            topbarConfig={topbarStoreConfig}
            rightSidebarConfig={rightSidebarStoreConfig}
            leftTopConfig={leftTopStoreConfig}
            leftBottomConfig={leftBottomStoreConfig}
            rightTopConfig={rightTopStoreConfig}
            rightBottomConfig={rightBottomStoreConfig}
            lockMainPanelResize={lockMainPanelResize}
        />
    );
};
